import { DestroyRef, inject, Injectable, NgZone } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DashboardProfileContextService, DashboardUserContextService } from '@rma/dashboard/authentication/public/user';
import { CurrentSubscriptionService } from '@rma/dashboard/billing/subscription-plans/public/api-subscription-features';
import { PlatformService } from '@rma/dashboard/generic/util/environment';
import { GenericFeatureFlagService } from '@rma/generic/ng/feature-flags/internal';
import { DeviceIdentificationService } from '@rma/generic/ng/util';
import { nonNullable } from '@rma/generic/rxjs/operators/non-nullable';
import { objectDistinctUntilChanged } from '@rma/generic/rxjs/operators/object-distinct-until-changed';
import { LDContextCommon, LDMultiKindContext } from 'launchdarkly-js-sdk-common';
import { combineLatest, filter, map, startWith } from 'rxjs';
import { FeatureUser, mapDataToFeatureUser } from '../util-feature-user-mapper/feature-user.mapper';

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagUserIdentificationService {
  private readonly profileContextService = inject(DashboardProfileContextService);
  private readonly userContextService = inject(DashboardUserContextService);
  private readonly currentSubscriptionService = inject(CurrentSubscriptionService);
  private readonly deviceIdService = inject(DeviceIdentificationService, {
    optional: true,
  });
  private readonly featureFlagService = inject(GenericFeatureFlagService<string>);
  private readonly platformService = inject(PlatformService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly zone = inject(NgZone);

  public initialise() {
    if (this.platformService.isPlatformServer) {
      return;
    }

    this.zone.runOutsideAngular(() => {
      combineLatest({
        profileContext: this.profileContextService.profileContext$.pipe(nonNullable()),
        userContext: this.userContextService.userContext$.pipe(nonNullable()),
        subscription: this.currentSubscriptionService.currentSubscription$.pipe(startWith(null)),
      })
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          map(({ profileContext, userContext, subscription }) => mapDataToFeatureUser(profileContext, userContext, subscription)),
          filter((user) => Boolean(user)),
          objectDistinctUntilChanged(),
        )
        .subscribe((user) => this.identifyUser(user));
    });
  }

  private identifyUser(currentUser: FeatureUser | null) {
    const deviceId = this.deviceIdService?.getDeviceId();

    const userContext = this.getLdUserContext(currentUser);
    const deviceContext = this.getLdDeviceContext(deviceId, Boolean(userContext?.anonymous));

    const context: LDMultiKindContext = {
      kind: 'multi',
    };

    if (deviceContext) {
      context['device'] = deviceContext;
    }

    if (userContext) {
      context['user'] = userContext;
    }

    // The LD client has already reported the failure, and nothing awaits this call, so a rejection
    // would only surface as an unhandled one (typically a fetch cancelled by navigation).
    this.featureFlagService.identify(context).catch(() => undefined);
  }

  private getLdDeviceContext(deviceId: string | undefined, isAnonymous: boolean): LDContextCommon | null {
    //If this works, we should extend the custom with browser information using bowser or ua parser
    return deviceId
      ? {
          key: deviceId,
          anonymous: isAnonymous,
          custom: {},
        }
      : null;
  }

  private getLdUserContext(currentUser: FeatureUser | null): LDContextCommon | null {
    return currentUser ? currentUser : { anonymous: true };
  }
}
